import { Box, Typography, SxProps, Theme, Stack } from '@mui/material';

interface SectionHeaderProps {
  overline?: string;
  title: string;
  subtitle?: string | React.ReactNode;
  centered?: boolean;
  variant?: 'light' | 'dark';
  sx?: SxProps<Theme>;
  action?: React.ReactNode;
  titleVariant?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

export function SectionHeader({
  overline,
  title,
  subtitle,
  centered = false,
  variant = 'light',
  sx,
  action,
  titleVariant = 'h2'
}: SectionHeaderProps) {
  const isDark = variant === 'dark';

  const headerContent = (
    <>
      {overline && (
        <Typography
          variant="overline"
          sx={{
            color: isDark ? 'secondary.light' : 'primary.main',
            fontWeight: 600,
            letterSpacing: 2,
            display: 'block',
            mb: 1,
          }}
        >
          {overline}
        </Typography>
      )}
      <Typography
        variant={titleVariant}
        component={titleVariant}
        sx={{
          color: isDark ? 'white' : undefined,
          fontWeight: 700,
          mb: 0 // Margin handled by parent stack or container
        }}
      >
        {title}
      </Typography>
    </>
  );

  const subtitleContent = subtitle && (
    <Typography
      variant="body1"
      sx={{
        color: isDark ? 'rgba(255,255,255,0.85)' : 'text.secondary',
        fontSize: '1.125rem',
        lineHeight: 1.7,
        '& span': { display: 'inline' }
      }}
      component="div"
    >
      {subtitle}
    </Typography>
  );

  if (action) {
    return (
      <Box sx={{ mb: 6, ...sx }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'flex-start' }}
          justifyContent="space-between"
          spacing={3}
          sx={{ mb: subtitle ? 2 : 0 }}
        >
          <Box sx={{ flex: 1, textAlign: centered ? 'center' : 'left' }}>
            {headerContent}
          </Box>
          <Box sx={{ flexShrink: 0, pt: { xs: 0, md: 1 } }}>
            {action}
          </Box>
        </Stack>
        {subtitleContent && (
          <Box sx={{ textAlign: centered ? 'center' : 'left' }}>
            {subtitleContent}
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        textAlign: centered ? 'center' : 'left',
        maxWidth: centered ? 700 : 'none',
        mx: centered ? 'auto' : 0,
        mb: 6,
        ...sx
      }}
    >
      {headerContent}
      {subtitleContent && <Box mt={2}>{subtitleContent}</Box>}
    </Box>
  );
}